import * as React from "react";

import { Box, Link, Typography } from "@material-ui/core";

import {
  useOnboardingStatusContext,
  useRunOnce,
  useTranslation,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { isZdo } from "client/pages/OnboardingStatus/utils";

import { StatusLabel } from "../StatusLabel";
import { StatusStep } from "../StatusStep";
import { useStyles } from "../styles";
import type {
  OnboardingStatusToCurrentStateMap,
  Status,
  StatusMap,
} from "../types";

const onboardingStatusToCurrentStateMap: OnboardingStatusToCurrentStateMap = {
  INELIGIBLE: "Declined",
  NO_RECORD: "None",
  IN_PROGRESS: "None",
  IN_REVIEW: "Reviewing",
  REQUIRES_ATTENTION: "RequiresAttention",
  READY_TO_INSTALL: "Done",
  COMPLETE: "Done",
};

export const ComplianceStep: React.FC = () => {
  const t = useTranslation();
  const classes = useStyles();
  const { onboardingStatus, onboardingGroup } = useOnboardingStatusContext();
  const currentState = onboardingStatusToCurrentStateMap[onboardingStatus];

  const statusMap: StatusMap = {
    None: {},
    InProgress: {},
    Done: {
      className: classes.dark,
    },
    Paused: {},
    Reviewing: {
      className: classes.dark,
      text: isZdo(onboardingGroup) ? "" : t("uiComplianceReviewSubheading"),
    },
    RequiresAttention: {
      className: classes.light,
      text: t("uiComplianceReviewRespondToEmailCopy"),
    },
    Declined: {
      className: classes.light,
      text: t("uiComplianceReviewDeclinedCopy"),
    },
  };

  const { className, text } = statusMap[currentState];

  const stepSpecificTextFromCurrentState = (
    currentState: Status
  ): string | null => {
    if (currentState === "RequiresAttention") {
      return "Respond to Email";
    } else if (currentState === "Declined") {
      return "Failed";
    }

    return null;
  };

  const stepSpecificText = stepSpecificTextFromCurrentState(currentState);

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_PAGE", "compliance_step_card_loaded")
  );

  return (
    <StatusStep timeToFade={500} activeOnboardingStep="Compliance">
      <Box className={`${className} ${classes.statusWrapper}`} mb={2} p={2}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>{t("uiComplianceReview")}</strong>
          </Typography>
          <StatusLabel
            status={currentState}
            stepSpecificText={stepSpecificText}
          />
        </Box>
        {text && (
          <Box mt={1}>
            <Typography variant="body2">
              {text}
              {currentState === "Declined" && (
                <Link
                  className={classes.link}
                  href={
                    "https://help.afterpay.com/hc/en-au/articles/900005681806-Why-does-Afterpay-decline-some-merchant-applications-"
                  }
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {" here "}
                </Link>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
